import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  getFinalBodyStructure,
  handleValidation,
  initialState,
} from "./Validator";
import {
  getCity,
  getCountry,
  getState,
  getTimeZone,
} from "../../../Team/components/stepper/Personal Details/PersonalDetailsService";
import customErrorHandler from "../../../../utils/customErrorHandler";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";
import {
  addOrganization,
  getOrganizationById,
  updateOrganization,
} from "./companyService";
import profileImg from "../../../../assets/images/user-default.svg";
import { ImageUpLoad } from "../../../Team/components/stepper/EmployeeDocuments/EmployeeDocumentServices";
import urls from "../../../../global/constants/UrlConstants";
import YhInput from "../../../../ui/YhInput";
import YhSelect from "../../../../ui/YhSelect";
import YhButton from "../../../../ui/YhButton";

interface CompanyInfoProps {
  isDialogOpen?: boolean;
  setCompanyOpen?: (open: boolean) => void;
  handleClose?: () => void;
  nextStep?: () => void;
  registerStepActions?: (actions: {
    submit: () => void | Promise<void>;
  }) => void;
}

const CompanyInfo = (props: CompanyInfoProps) => {
  const history = useHistory();
  const isDialogMode = typeof props.isDialogOpen === "boolean";

  const [companyDetail, setCompanyDetail] = useState<any>(initialState({}));
  const [previewImage, setPreviewImage] = useState<any>(profileImg);
  const [country, setCountry] = useState<any[]>([]);
  const [state, setstate] = useState<any[]>([]);
  const [city, setCity] = useState<any[]>([]);
  const [isdataHere, setIsDataHere] = useState<boolean>(true);
  const [timeZone, setTimeZone] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const response: any = await getOrganizationById();
      customErrorHandler(response);
      response.data ? setIsDataHere(true) : setIsDataHere(false);
      setPreviewImage(response.data?.imageUrl || profileImg);
      setCompanyDetail(initialState(response.data || {}));
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleClose = () => {
    if (props.setCompanyOpen) {
      props.setCompanyOpen(false);
      return;
    }
    if (props.handleClose) {
      props.handleClose();
      return;
    }
    history.push(urls.accountSettingsViewPth);
  };

  const handleOnChangeInputField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCompanyDetail({
      ...companyDetail,
      [event.target.name]: { value: event.target.value, error: "" },
    });
  };

  const handleAutocompleteChange = (name: string, key: any, value: any) => {
    if (!value) return;
    if (key === "country_name") {
      setCompanyDetail((prevState: any) => ({
        ...prevState,
        [name]: { value: value[key], error: "", id: value._id },
        country_code: { value: value.country_code, error: "" },
        currency: { value: value.currency, error: "" },
      }));
    }
    if (key === "state_name" || key === "city_name" || key === "time_zone") {
      setCompanyDetail((prevState: any) => ({
        ...prevState,
        [name]: { value: value[key], error: "", id: value._id },
      }));
    }
  };

  const submitForm = async () => {
    const validatedObject: any = handleValidation(companyDetail);
    if (
      validatedObject.companyname.error ||
      validatedObject.brandname.error ||
      validatedObject.country_id.error ||
      validatedObject.time_zone.error ||
      validatedObject.state_id.error ||
      validatedObject.city.error ||
      validatedObject.address1.error ||
      validatedObject.address2.error ||
      validatedObject.zipcode.error ||
      validatedObject.website.error
    ) {
      setCompanyDetail({ ...validatedObject });
      return;
    }

    try {
      setSaving(true);
      const response: any = isdataHere
        ? await updateOrganization(getFinalBodyStructure(companyDetail))
        : await addOrganization(getFinalBodyStructure(companyDetail));
      customErrorHandler(response);
      openSuccessNotification(response?.message);
      handleClose();
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSaving(false);
    }
    props.nextStep?.();
  };

  useEffect(() => {
    props.registerStepActions?.({ submit: submitForm });
  });

  const onClickAutoComplet = async (inputType: string) => {
    if (inputType === "country_id") {
      try {
        const response = await getCountry();
        customErrorHandler(response);
        setCountry(response.data || []);
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
        );
      }
    }
    if (inputType === "state_id") {
      try {
        const response = await getState(companyDetail.country_id.id);
        customErrorHandler(response);
        setstate(response.data || []);
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
        );
      }
    }
    if (inputType === "city") {
      try {
        const response = await getCity(companyDetail.state_id.id);
        customErrorHandler(response);
        setCity(response.data || []);
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
        );
      }
    }
    if (inputType === "time_zone") {
      try {
        const response = await getTimeZone(companyDetail.country_id.id);
        customErrorHandler(response);
        setTimeZone(response.data || []);
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
        );
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    const data = new FormData();
    data.append("file", file);

    reader.onload = async (loadEvent: any) => {
      setPreviewImage(loadEvent.target.result);
      try {
        const uploadedUrl = await ImageUpLoad(data);
        setCompanyDetail((prevFormData: any) => ({
          ...prevFormData,
          imageUrl: { error: "", value: uploadedUrl?.imageUrl },
        }));
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
        );
      }
    };

    reader.readAsDataURL(file);
  };

  const countryId = companyDetail?.country_id?.id || "";
  const stateId = companyDetail?.state_id?.id || "";
  const timeZoneId = companyDetail?.time_zone?.id || "";
  const logoSrc =
    companyDetail?.imageUrl?.value &&
    companyDetail.imageUrl.value !== "Https//:"
      ? companyDetail.imageUrl.value
      : previewImage || profileImg;

  const formBody = (
    <div
      className={`relative flex flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0 ${
        isDialogMode
          ? "h-[min(90vh,880px)] w-full max-w-5xl"
          : "h-[calc(100vh-68px)] max-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
            Organization Setup
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Manage your company information to use the platform
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Close"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <section className="min-h-0 min-w-0 flex-1 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-[#111827]">
              Company Details
            </h2>
            <p className="mt-1 text-sm text-[#6B7280]">
              Update legal name, brand, address and logo
            </p>
          </div>
          <div className="relative">
            <div className="h-20 w-20 overflow-hidden rounded-full border border-[#E5E7EB] bg-[#F3F4F6]">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt="Company logo"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-xs text-[#6B7280]">
                  Upload
                </span>
              )}
            </div>
            <button
              type="button"
              className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#374151] shadow-sm"
              onClick={handleUploadClick}
              aria-label="Upload logo"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 15.2A3.2 3.2 0 1 0 12 8.8a3.2 3.2 0 0 0 0 6.4zm8-5.2h-1.2l-.9-1.5A2 2 0 0 0 16.2 7H7.8a2 2 0 0 0-1.7 1.5L5.2 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <YhInput
            label="Company Type"
            value={companyDetail?.company_type?.value || ""}
            disabled
            className="bg-[#E7E7EA]"
          />

          <YhInput
            label="Company Legal Name"
            name="companyname"
            placeholder="eg. XYZ LLC"
            onChange={handleOnChangeInputField}
            value={companyDetail?.companyname?.value || ""}
            error={companyDetail?.companyname?.error}
          />

          <YhInput
            label="Company Brand Name"
            name="brandname"
            placeholder="eg. apple"
            onChange={handleOnChangeInputField}
            value={companyDetail?.brandname?.value || ""}
            error={companyDetail?.brandname?.error}
          />

          <YhSelect
            label="Default Time Zone"
            placeholder="Select time zone"
            value={timeZoneId}
            error={companyDetail?.time_zone?.error}
            options={timeZone.map((tz: any) => ({
              label: tz.time_zone,
              value: tz._id,
            }))}
            disabled={!countryId && !country.length}
            onFocus={() => onClickAutoComplet("time_zone")}
            onChange={(e) => {
              const selected =
                timeZone.find((tz: any) => tz._id === e.target.value) || null;
              if (!selected) return;
              handleAutocompleteChange("time_zone", "time_zone", selected);
            }}
          />

          <YhSelect
            label="Country"
            placeholder="Select country"
            value={countryId}
            error={companyDetail?.country_id?.error}
            options={country.map((c: any) => ({
              label: c.country_name,
              value: c._id,
            }))}
            onFocus={() => onClickAutoComplet("country_id")}
            onChange={(e) => {
              const selected =
                country.find((c: any) => c._id === e.target.value) || null;
              if (!selected) return;
              handleAutocompleteChange("country_id", "country_name", selected);
              setstate([]);
              setTimeZone([]);
              setCity([]);
            }}
          />

          <YhSelect
            label="State"
            placeholder="Select state"
            value={stateId}
            error={companyDetail?.state_id?.error}
            options={state.map((s: any) => ({
              label: s.state_name,
              value: s._id,
            }))}
            disabled={!countryId && !country.length}
            onFocus={() => onClickAutoComplet("state_id")}
            onChange={(e) => {
              const selected =
                state.find((s: any) => s._id === e.target.value) || null;
              if (!selected) return;
              handleAutocompleteChange("state_id", "state_name", selected);
              setCity([]);
            }}
          />

          <YhInput
            label="City"
            name="city"
            placeholder="enter your city"
            onChange={handleOnChangeInputField}
            value={companyDetail?.city?.value || ""}
            error={companyDetail?.city?.error}
          />

          <YhInput
            label="Address 1"
            name="address1"
            placeholder="Address 1"
            onChange={handleOnChangeInputField}
            value={companyDetail?.address1?.value || ""}
            error={companyDetail?.address1?.error}
          />

          <YhInput
            label="Address 2"
            name="address2"
            placeholder="Address 2"
            onChange={handleOnChangeInputField}
            value={companyDetail?.address2?.value || ""}
            error={companyDetail?.address2?.error}
          />

          <YhInput
            type="text"
            label="Zip code"
            name="zipcode"
            placeholder="111111"
            onChange={handleOnChangeInputField}
            value={companyDetail?.zipcode?.value || ""}
            error={companyDetail?.zipcode?.error}
          />

          <YhInput
            label="Company Website"
            name="website"
            placeholder="www.pulse.com"
            onChange={handleOnChangeInputField}
            value={companyDetail?.website?.value || ""}
            error={companyDetail?.website?.error}
          />
        </div>
      </section>

      <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
        <YhButton variant="secondary" size="sm" onClick={handleClose}>
          Back
        </YhButton>
        <YhButton
          variant="primary"
          size="sm"
          loading={saving}
          onClick={submitForm}
        >
          Save
        </YhButton>
      </div>
    </div>
  );

  if (isDialogMode) {
    if (!props.isDialogOpen) return null;
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-5xl overflow-hidden rounded-2xl shadow-xl">
          {formBody}
        </div>
      </div>
    );
  }

  return formBody;
};

export default CompanyInfo;
